import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { getAllRefunds, createRefund } from '../../actions/refund';
import { 
    Row, 
    Col, 
    Card, 
    Button, 
    Form, 
    Alert, 
    Spinner, 
    Table, 
    Badge,
    Modal,
    Pagination,
    InputGroup
} from 'react-bootstrap';
import { 
    RefreshCw,
    Search,
    Plus,
    IndianRupee,
    Calendar,
    FileText,
    CheckCircle,
    Clock,
    XCircle
} from 'lucide-react';

const Refunds = ({ 
    getAllRefunds, 
    createRefund,
    refunds, 
    loading, 
    error,
    pagination
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters] = useState({
        paymentId: ''
    });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createFormData, setCreateFormData] = useState({
        paymentId: '',
        amount: '',
        notes: '',
        speed: 'normal'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const itemsPerPage = 10;

    useEffect(() => {
        document.title = 'Refunds Management';
        fetchRefunds();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const fetchRefunds = () => {
        const skip = (currentPage - 1) * itemsPerPage;
        getAllRefunds({
            count: itemsPerPage,
            skip,
            ...(filters.paymentId && { paymentId: filters.paymentId })
        });
    };

    const handleFilterChange = (e) => {
        setFilters({
            ...filters,
            [e.target.name]: e.target.value
        });
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchRefunds();
    };

    const handleClearFilters = () => {
        setFilters({ paymentId: '' });
        setCurrentPage(1);
        getAllRefunds({ count: itemsPerPage, skip: 0 });
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleCreateRefund = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            const refundData = {
                paymentId: createFormData.paymentId,
                speed: createFormData.speed,
                ...(createFormData.amount && { amount: parseFloat(createFormData.amount) }),
                ...(createFormData.notes && { notes: createFormData.notes })
            };
            await createRefund(refundData);
            setShowCreateModal(false);
            setCreateFormData({
                paymentId: '',
                amount: '',
                notes: '',
                speed: 'normal'
            });
            fetchRefunds();
        } catch (err) {
            // Error handled by action
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            processed: { variant: 'success', icon: <CheckCircle size={14} className="me-1" /> },
            pending: { variant: 'warning', icon: <Clock size={14} className="me-1" /> },
            failed: { variant: 'danger', icon: <XCircle size={14} className="me-1" /> }
        };
        const config = statusConfig[status?.toLowerCase()] || { variant: 'secondary', icon: null };
        return (
            <Badge bg={config.variant} className="d-flex align-items-center" style={{ width: 'fit-content' }}>
                {config.icon}
                {status || 'Unknown'}
            </Badge>
        );
    };

    const renderPagination = () => {
        if (!pagination || pagination.total <= itemsPerPage) return null;

        const totalPages = Math.ceil(pagination.total / itemsPerPage);
        const items = [];
        const maxVisiblePages = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return (
            <div className="d-flex justify-content-center mt-4">
                <Pagination>
                    <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {items}
                    <Pagination.Next
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                    />
                </Pagination>
            </div>
        );
    };

    return (
        <Fragment>
            <div className="container-fluid py-4" style={{background: '#f8fafc', minHeight: '100vh'}}>
                {/* Header */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <div>
                            <h2 className="text-primary mb-1">
                                <IndianRupee size={32} className="me-3" />
                                Refunds Management
                            </h2>
                            <p className="text-muted mb-0">
                                Manage Razorpay refunds for payments
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-primary"
                                onClick={fetchRefunds}
                                disabled={loading}
                            >
                                <RefreshCw size={16} className="me-2" />
                                Refresh
                            </Button>
                            <Button
                                variant="success"
                                onClick={() => setShowCreateModal(true)}
                                disabled={loading}
                            >
                                <Plus size={16} className="me-2" />
                                Create Refund
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Error Alert */}
                {error && (
                    <Alert variant="danger" dismissible>
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                )}

                {/* Filters */}
                <Card className="mb-4 shadow-sm">
                    <Card.Header className="bg-light">
                        <h5 className="mb-0">
                            <i className="fas fa-filter me-2"></i>
                            Filters
                        </h5>
                    </Card.Header>
                    <Card.Body>
                        <Form onSubmit={handleFilterSubmit}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Payment ID</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <Search size={16} />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                name="paymentId"
                                                placeholder="Filter by payment ID"
                                                value={filters.paymentId}
                                                onChange={handleFilterChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6} className="d-flex align-items-end">
                                    <div className="d-flex gap-2 w-100">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            disabled={loading}
                                            className="flex-fill"
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Searching...
                                                </>
                                            ) : (
                                                <>
                                                    <Search size={16} className="me-2" />
                                                    Apply Filter
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline-secondary"
                                            onClick={handleClearFilters}
                                            disabled={loading}
                                        >
                                            Clear
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>

                {/* Loading State */}
                {loading && (
                    <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Loading refunds...</p>
                        </div>
                    </div>
                )}

                {/* Refunds Table */}
                {!loading && (
                    <Card className="shadow-sm">
                        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="fas fa-table me-2"></i>
                                All Refunds
                            </h5>
                            {pagination && (
                                <Badge bg="light" text="dark" className="fs-6">
                                    {pagination.total || 0} total refunds
                                </Badge>
                            )}
                        </Card.Header>
                        <Card.Body className="p-0">
                            {refunds && refunds.length > 0 ? (
                                <>
                                    <div className="table-responsive">
                                        <Table striped hover responsive className="mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Refund ID</th>
                                                    <th>Payment ID</th>
                                                    <th>Order</th>
                                                    <th>Amount (₹)</th>
                                                    <th>Status</th>
                                                    <th>Speed</th>
                                                    <th>Receipt</th>
                                                    <th>Created At</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {refunds.map((refund) => (
                                                    <tr key={refund.refundId}>
                                                        <td>
                                                            <Link
                                                                to={`/inventory/v1.2/refunds/${refund.refundId}`}
                                                                className="text-decoration-none fw-bold text-primary"
                                                            >
                                                                {refund.refundId}
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            <Link
                                                                to={`/inventory/v1.2/payments/${refund.paymentId}/refunds`}
                                                                className="text-decoration-none"
                                                            >
                                                                <div className="fw-bold text-primary">
                                                                    {refund.paymentId?.slice(0, 8) || 'N/A'}
                                                                </div>
                                                                <code className="text-muted small">
                                                                    {refund.paymentId}
                                                                </code>
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            {refund.orderId ? (
                                                                <Link
                                                                    to={`/inventory/v1.2/orders/${refund.orderId}`}
                                                                    className="text-decoration-none fw-bold text-primary"
                                                                >
                                                                    {refund.order || refund.orderId?.slice(-6) || 'N/A'}
                                                                </Link>
                                                            ) : (
                                                                <span className="text-muted">N/A</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className="fw-bold text-success">
                                                                ₹{refund.amount?.toLocaleString() || '0.00'}
                                                            </span>
                                                        </td>
                                                        <td>{getStatusBadge(refund.status)}</td>
                                                        <td>
                                                            <Badge bg="info">
                                                                {refund.speed || 'normal'}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            {refund.receipt ? (
                                                                <code>{refund.receipt}</code>
                                                            ) : (
                                                                <span className="text-muted">N/A</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <Calendar size={14} className="me-1 text-muted" />
                                                                {formatDate(refund.createdAt)}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <Button
                                                                as={Link}
                                                                to={`/inventory/v1.2/refunds/${refund.refundId}`}
                                                                variant="outline-primary"
                                                                size="sm"
                                                            >
                                                                <FileText size={14} className="me-1" />
                                                                View
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                    {renderPagination()}
                                </>
                            ) : (
                                <div className="text-center py-5">
                                    <IndianRupee size={48} className="text-muted mb-3" />
                                    <p className="text-muted">No refunds found.</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                )}

                {/* Create Refund Modal */}
                <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
                    <Modal.Header closeButton className="bg-success text-white">
                        <Modal.Title>
                            <i className="fas fa-plus-circle me-2"></i>
                            Create Refund
                        </Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleCreateRefund}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Payment ID <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Razorpay payment ID (e.g., pay_XXXXXXXXXXXXX)"
                                    value={createFormData.paymentId}
                                    onChange={(e) => setCreateFormData({...createFormData, paymentId: e.target.value})}
                                    required
                                    disabled={isSubmitting}
                                />
                                <Form.Text className="text-muted">
                                    The Razorpay payment ID for which refund should be created
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Refund Amount (₹)</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    placeholder="Leave empty for full refund"
                                    value={createFormData.amount}
                                    onChange={(e) => setCreateFormData({...createFormData, amount: e.target.value})}
                                    disabled={isSubmitting}
                                />
                                <Form.Text className="text-muted">
                                    Enter amount for partial refund. Leave empty to process full refund.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Notes</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Enter refund reason or notes (optional)"
                                    value={createFormData.notes}
                                    onChange={(e) => setCreateFormData({...createFormData, notes: e.target.value})}
                                    disabled={isSubmitting}
                                />
                                <Form.Text className="text-muted">
                                    Optional notes or reason for the refund
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Refund Speed</Form.Label>
                                <Form.Select
                                    value={createFormData.speed}
                                    onChange={(e) => setCreateFormData({...createFormData, speed: e.target.value})}
                                    disabled={isSubmitting}
                                >
                                    <option value="normal">Normal</option>
                                    <option value="optimum">Optimum (Faster)</option>
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    Optimum speed may have additional charges
                                </Form.Text>
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowCreateModal(false)}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="success"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Plus size={16} className="me-2" />
                                        Create Refund
                                    </>
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </div>
        </Fragment>
    );
};

Refunds.propTypes = {
    getAllRefunds: PropTypes.func.isRequired,
    createRefund: PropTypes.func.isRequired,
    refunds: PropTypes.array,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.string,
    pagination: PropTypes.object
};

const mapStateToProps = state => ({
    refunds: state.refund.refunds,
    loading: state.refund.loading,
    error: state.refund.error,
    pagination: state.refund.pagination
});

export default connect(mapStateToProps, { getAllRefunds, createRefund })(Refunds);

